import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import {
  AccentButton,
  GreenGradientButton,
  NeutralButton,
  NeutralGradientButton,
} from '../../shared/Buttons';
import { Entity } from '../../types/apiTypes';
import Tooltip from '../dashboard/customers/tabs/Tooltip';
import { useState } from 'react';

interface SignupFooterProps {
  currStep: string;
  selectedRole: Entity;
  disableSubmit?: boolean;
  onSubmitClick?: () => void;
  onBackClick?: () => void;
  isMobile?: boolean;
  showTooltip?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function SignupFooter({
  currStep,
  selectedRole,
  disableSubmit,
  onSubmitClick,
  onBackClick,
  isMobile,
  showTooltip,
  onMouseEnter,
  onMouseLeave,
}: SignupFooterProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const renderButtons = (role: Entity) => {
    if (role === Entity.Customer) {
      switch (currStep) {
        case 'business_info':
          return (
            <div className="flex flex-col items-center justify-center gap-y-3">
              <GreenGradientButton
                submit={true}
                disabled={disableSubmit}
                onClick={(e) => {
                  if (!disableSubmit && onSubmitClick) {
                    setIsSubmitting(true);
                    onSubmitClick();
                    setTimeout(() => setIsSubmitting(false), 2000);
                  }
                }}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  <>
                    <span>Continue</span>
                    <ArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
                <div className="absolute inset-0 rounded-full bg-white opacity-0 transition-opacity group-hover:opacity-10" />
              </GreenGradientButton>
              <p className="text-xs text-gray-500">
                Takes only a few minutes to complete
              </p>
            </div>
          );
        case 'personal_info':
        case 'review':
        case 'choose_plan':
          return (
            <div className="flex flex-row items-center justify-center gap-x-4">
              <NeutralGradientButton submit={false} onClick={onBackClick}>
                <ArrowLeft
                  size={18}
                  className="transition-transform group-hover:-translate-x-1"
                />
                <span>Back</span>
              </NeutralGradientButton>

              <div
                className="relative"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                <Tooltip
                  show={!!showTooltip && !!disableSubmit}
                  message={
                    currStep === 'choose_plan'
                      ? 'Please select a tier to continue'
                      : 'Please complete all required fields'
                  }
                  position="top"
                  className="!left-1/2 !w-max !max-w-[240px] !-translate-x-1/2"
                />
                <GreenGradientButton
                  submit={true}
                  disabled={disableSubmit}
                  addClass={`${isMobile ? 'min-w-[120px]' : 'min-w-[200px]'}`}
                  onClick={(e) => {
                    if (!disableSubmit && onSubmitClick) {
                      setIsSubmitting(true);
                      onSubmitClick();
                      setTimeout(() => setIsSubmitting(false), 2000);
                    }
                  }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <>
                      <span>Continue</span>
                      <ArrowRight
                        size={18}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </GreenGradientButton>
              </div>
            </div>
          );
        // bank_agreement cannot actually reach here: signup.tsx remaps it to
        // master_service_agreement before currStep is ever set (see DEPRECATED_STEP_REMAP).
        // Kept as a fall-through so the footer matches the screen if that remap ever moves.
        case 'bank_agreement':
        case 'master_service_agreement':
          return (
            <div className="flex flex-row items-center justify-center">
              <div
                className="relative"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                <Tooltip
                  show={!!showTooltip && !!disableSubmit}
                  message="Please enter your name and check both boxes"
                  position="top"
                  className="!left-1/2 !w-max !max-w-[240px] !-translate-x-1/2"
                />
                <GreenGradientButton
                  submit={true}
                  disabled={disableSubmit}
                  addClass={`${isMobile ? 'min-w-[120px]' : 'min-w-[200px]'}`}
                  onClick={(e) => {
                    if (!disableSubmit && onSubmitClick) {
                      setIsSubmitting(true);
                      onSubmitClick();
                      setTimeout(() => setIsSubmitting(false), 2000);
                    }
                  }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <>
                      <span>Continue</span>
                      <ArrowRight
                        size={18}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </GreenGradientButton>
              </div>
            </div>
          );
        case 'assets':
          return (
            <div className="flex flex-col items-center justify-center gap-y-[12px] align-middle">
              <GreenGradientButton
                submit={true}
                disabled={disableSubmit}
                onClick={onSubmitClick}
                addClass={`${isMobile ? 'min-w-[120px]' : 'min-w-[200px]'}`}
              >
                Next
              </GreenGradientButton>
            </div>
          );
        case 'plaid_link':
          return (
            <div className="flex flex-col items-center justify-center gap-y-[12px] align-middle">
              <NeutralButton
                addClass="rounded-[4px] w-[250px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle"
                onClick={onBackClick}
              >
                <ArrowLeft size={20} className="text-graysecondary" />{' '}
                <h2>Back</h2>
              </NeutralButton>
            </div>
          );
        // case 6:
        //   return (
        //     <div className="flex flex-col items-center justify-center gap-y-[12px] align-middle">
        //       <AccentButton
        //         submit={true}
        //         disabled={disableSubmit}
        //         addClass="rounded-full w-[250px] h-[48px]"
        //         onClick={onSubmitClick}
        //       >
        //         Next
        //       </AccentButton>
        //     </div>
        //   );
      }
    } else {
      switch (currStep) {
        case 'partner_business_info':
        case 'vendor_business_info':
          return (
            <div className="flex flex-col items-center justify-center gap-y-[12px] align-middle">
              <AccentButton
                submit={true}
                disabled={disableSubmit}
                addClass="disabled:cursor-not-allowed disabled:opacity-[50%] rounded-full w-[250px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle"
                onClick={onSubmitClick}
              >
                Next
              </AccentButton>
              {/* <div className="flex flex-row items-center space-x-1 align-middle text-[16px]">
                <h2 className="text-[#8B919F]">Already have an account?</h2>
                <h2
                  className="text-accent hover:cursor-pointer"
                  onClick={() => navigate('/login')}
                >
                  Log In
                </h2>
              </div> */}
            </div>
          );
        case 'partner_personal_info':
        case 'partner_management_contact':
        case 'vendor_personal_info':
        case 'vendor_management_contact':
          return (
            <div className="flex flex-row items-center justify-center gap-x-[12px] align-middle">
              <NeutralButton
                addClass={`rounded-full ${isMobile ? 'w-[125px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle' : 'w-[250px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle'}`}
                onClick={onBackClick}
              >
                <ArrowLeft size={20} className="text-graysecondary" />{' '}
                <h2>Back</h2>
              </NeutralButton>
              <AccentButton
                submit={true}
                disabled={disableSubmit}
                addClass={`disabled:cursor-not-allowed disabled:opacity-[50%] rounded-full ${isMobile ? 'w-[125px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle' : 'w-[250px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle'} disabled:cursor-not-allowed disabled:opacity-[50%]`}
                onClick={onSubmitClick}
              >
                Next
              </AccentButton>
            </div>
          );
        case 'partner_contract':
        case 'vendor_contract':
          return (
            <div className="flex flex-row items-center justify-center gap-x-[12px] align-middle">
              <NeutralButton
                addClass={`rounded-full ${isMobile ? 'w-[125px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle' : 'w-[250px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle'}`}
                onClick={onBackClick}
              >
                <ArrowLeft size={20} className="text-graysecondary" />{' '}
                <h2>Back</h2>
              </NeutralButton>
              <AccentButton
                submit={true}
                disabled={disableSubmit}
                addClass={`disabled:cursor-not-allowed disabled:opacity-[50%] rounded-full ${isMobile ? 'w-[125px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle' : 'w-[250px] h-[48px] flex flex-row items-center justify-center space-x-2 align-middle'} disabled:cursor-not-allowed disabled:opacity-[50%]`}
                onClick={onSubmitClick}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit Application'
                )}
              </AccentButton>
            </div>
          );
      }
    }
  };

  return (
    <div className={``}>
      {/* Main footer with buttons */}
      <div className={`flex flex-col items-center justify-center pt-3`}>
        {renderButtons(selectedRole)}
      </div>
    </div>
  );
}

export default SignupFooter;
